import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight } from "lucide-react";
import api from "../api";
import { Reveal } from "../components/motion";

function inlineMd(text) {
  return text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/`([^`]+)`/g, '<code class="font-mono text-volt bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-[0.85em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
}

function DocContent({ content }) {
  const blocks = content.split("\n\n");
  return (
    <div className="space-y-6" data-testid="doc-content">
      {blocks.map((block, i) => (
        <div key={i} className="space-y-3">
          {block.split("\n").map((line, j) =>
            line.startsWith("## ") ? (
              <h3
                key={j}
                className="font-heading text-xl font-bold text-white pt-3 border-b border-white/10 pb-3"
                dangerouslySetInnerHTML={{ __html: inlineMd(line.slice(3)) }}
              />
            ) : (
              <p key={j} className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineMd(line) }} />
            )
          )}
        </div>
      ))}
    </div>
  );
}

export default function Docs() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/docs").then((r) => {
      setDocs(r.data);
      if (!slug && r.data.length) navigate(`/docs/${r.data[0].slug}`, { replace: true });
    }).catch(() => {});
  }, [slug, navigate]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/public/docs/${slug}`)
      .then((r) => setCurrent(r.data))
      .catch(() => setCurrent(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const grouped = useMemo(() => {
    const g = {};
    docs.forEach((d) => {
      g[d.category] = g[d.category] || [];
      g[d.category].push(d);
    });
    return g;
  }, [docs]);

  return (
    <main className="pt-16 min-h-screen" data-testid="docs-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[280px_1fr] gap-12">
        <aside>
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <BookOpen size={20} className="text-blurple" />
              <h1 className="font-heading text-2xl font-bold text-white">Documentation</h1>
            </div>
            <nav className="space-y-7" data-testid="docs-nav">
              {Object.entries(grouped).map(([cat, pages]) => (
                <div key={cat}>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-3">{cat}</p>
                  <ul className="space-y-1">
                    {pages.map((p) => (
                      <li key={p.id}>
                        <Link
                          to={`/docs/${p.slug}`}
                          data-testid={`docs-link-${p.slug}`}
                          className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                            slug === p.slug ? "bg-blurple/15 text-white border border-blurple/30" : "text-muted-foreground hover:text-white hover:bg-white/5 border border-transparent"
                          }`}
                        >
                          {p.title}
                          <ChevronRight size={13} className={slug === p.slug ? "text-blurple" : "text-white/20"} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </Reveal>
        </aside>

        <div className="min-w-0">
          {loading ? (
            <p className="text-white/40 font-mono text-sm">Loading…</p>
          ) : current ? (
            <Reveal key={current.id}>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-volt mb-3">{current.category}</p>
              <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight mb-10" style={{ fontWeight: 800 }} data-testid="doc-title">
                {current.title}
              </h2>
              <DocContent content={current.content} />
            </Reveal>
          ) : (
            <p className="text-white/40" data-testid="doc-not-found">Page not found.</p>
          )}
        </div>
      </div>
    </main>
  );
}
